<script setup lang="ts">
import { computed, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import PageLink from "~/components/text/plugins/PageLink.vue";
import { getResolvedFileMediaType } from "~/constants/mimeType";
import {
  getModelsSupportingAttachmentMediaType,
  isAttachmentMediaTypeSupportedByModel,
  MODEL_TO_DISPLAY_NAME,
} from "~/constants/model";
import { AttachmentFieldIcon, DownloadIcon, ImageIcon } from "~/icons";
import { type AiModel, type AiModelWithoutAuto, ButtonStyle, IconSize, PageLinkMode } from "~/shared/enums";
import { useDataStore } from "~/stores";
import { downloadFile } from "~/utils/api";
import { asyncState } from "~/utils/async";
import { makeUuid, prettyFormatList } from "~/utils/common";
import { filePreviews } from "~/utils/fileUpload";
import { isImageType } from "~/utils/media";
import { getOrdersBetween } from "~/utils/orderManager";

type UnsupportedAttachment = {
  duid: string;
  name: string;
  kind: string;
};

const TOOLTIP_CLASS = "!inline-flex align-middle";
const CHIP_CLASS = "!inline-flex min-w-0 align-middle text-xs";
const INCOMPATIBLE_CHIP_CLASS = "border border-danger-base";
const INCOMPATIBLE_ICON_CLASS = "icon-xs text-danger-base";
const UNSUPPORTED_FILE_TYPE_TEXT = "This file type isn't supported by any available model";

const props = defineProps<{
  attachmentDuids: string[];
  chatDuid?: string;
  messageModel?: AiModel | AiModelWithoutAuto;
  skillDuid?: string;
  mode?: PageLinkMode;
}>();

const dataStore = useDataStore();

const { state: attachments } = asyncState(() => dataStore.getAttachmentsByDuidsOrdered(props.attachmentDuids), {
  default: dataStore.getAttachmentsByDuidsOrderedIfLoaded(props.attachmentDuids),
  watch: () => props.attachmentDuids,
});

const areAttachmentsCreated = ref(true);
const unsupportedAttachments = ref<UnsupportedAttachment[]>([]);

const isAttachmentUploading = computed(
  () => !areAttachmentsCreated.value || attachments.value.some((attachment) => filePreviews.has(attachment.duid))
);

const isIncompatible = (kind: string) =>
  !!props.messageModel && !isAttachmentMediaTypeSupportedByModel(props.messageModel, kind);

const getIncompatibleTooltipText = (kind: string) => {
  const modelNames = getModelsSupportingAttachmentMediaType(kind).map((m) => MODEL_TO_DISPLAY_NAME.get(m) ?? "");
  if (modelNames.length === 0) {
    return UNSUPPORTED_FILE_TYPE_TEXT;
  }
  return `This file type isn't supported by this model, switch to ${prettyFormatList(modelNames, { finalSeparator: "or" })}`;
};

const updateChatAttachments = async (attachmentDuids: string[], shouldRemove: boolean) => {
  const chat = dataStore.getChatByDuidIfLoaded(props.chatDuid ?? "");
  const draft = dataStore.getDraftMessageByChatDuid(props.chatDuid ?? "");
  if (!chat || !draft) {
    return;
  }
  await dataStore.updateChatDraftMessage(chat.duid, {
    ...draft,
    attachmentDuids: shouldRemove
      ? draft.attachmentDuids.filter((e) => !new Set(attachmentDuids).has(e))
      : [...draft.attachmentDuids, ...attachmentDuids],
  });
};

const updateSkillAttachments = async (attachmentDuids: string[], shouldRemove: boolean) => {
  const skill = dataStore.getSkillByDuid(props.skillDuid ?? "");
  if (!skill) {
    return;
  }
  await dataStore.updateSkill({
    duid: skill.duid,
    attachmentDuids: shouldRemove
      ? skill.attachmentDuids.filter((e) => !new Set(attachmentDuids).has(e))
      : [...skill.attachmentDuids, ...attachmentDuids],
  });
};

const addAttachments = async (files: File[]) => {
  if (files.length === 0) {
    return;
  }

  const filesWithKinds = files.map((file) => ({ file, kind: getResolvedFileMediaType(file) }));
  const unsupportedFiles = filesWithKinds.filter(
    ({ kind }) => getModelsSupportingAttachmentMediaType(kind).length === 0
  );
  if (unsupportedFiles.length > 0) {
    unsupportedAttachments.value = [
      ...unsupportedAttachments.value,
      ...unsupportedFiles.map(({ file, kind }) => ({
        duid: makeUuid(),
        name: file.name,
        kind,
      })),
    ];
  }
  const supportedFiles = filesWithKinds
    .filter(({ kind }) => getModelsSupportingAttachmentMediaType(kind).length > 0)
    .map(({ file }) => file);
  if (supportedFiles.length === 0) {
    return;
  }
  areAttachmentsCreated.value = false;

  const lastAttachment = attachments.value.at(-1);
  const orders = getOrdersBetween(lastAttachment?.order, undefined, supportedFiles.length);
  const attachmentCreates = supportedFiles.map((file, i) => ({ file, order: orders[i] }));

  try {
    const attachmentAndCreates = await dataStore.createAttachments(attachmentCreates);
    const duids = attachmentAndCreates.map((e) => e.create.duid);
    await updateChatAttachments(duids, false);
    await updateSkillAttachments(duids, false);
  } finally {
    areAttachmentsCreated.value = true;
  }
};

const removeAttachment = async (duid: string) => {
  await updateChatAttachments([duid], true);
  await updateSkillAttachments([duid], true);
};

const removeIncompatibleAttachments = async () => {
  if (!props.messageModel) {
    return;
  }

  const incompatibleAttachmentDuids = attachments.value
    .filter((attachment) => isIncompatible(attachment.kind))
    .map((attachment) => attachment.duid);
  if (incompatibleAttachmentDuids.length > 0) {
    await updateChatAttachments(incompatibleAttachmentDuids, true);
  }

  unsupportedAttachments.value = [];
};

const removeUnsupportedAttachment = (duid: string) => {
  unsupportedAttachments.value = unsupportedAttachments.value.filter((a) => a.duid !== duid);
};
watch(
  () => [props.chatDuid, props.skillDuid],
  () => {
    unsupportedAttachments.value = [];
  }
);

defineExpose({
  addAttachments,
  removeIncompatibleAttachments,
  isAttachmentUploading,
});
</script>

<template>
  <Tooltip
    v-for="attachment in attachments"
    :key="attachment.duid"
    :disabled="!isIncompatible(attachment.kind)"
    :text="getIncompatibleTooltipText(attachment.kind)"
    :class="TOOLTIP_CLASS">
    <PageLink
      :mode="mode"
      :icon="isImageType(attachment.kind) ? ImageIcon : AttachmentFieldIcon"
      :icon-args="{ class: isIncompatible(attachment.kind) ? INCOMPATIBLE_ICON_CLASS : 'icon-xs' }"
      :title="attachment.name"
      not-in-text
      shrink
      :class="[CHIP_CLASS, '!cursor-default', isIncompatible(attachment.kind) ? INCOMPATIBLE_CHIP_CLASS : '']"
      @remove="() => removeAttachment(attachment.duid)">
      <template #actions>
        <Tooltip text="Download">
          <Button
            class="!p-0.5 hover:!bg-md"
            :btn-style="ButtonStyle.CHIP"
            :icon="DownloadIcon"
            borderless
            :icon-size="IconSize.XS"
            a11y-label="Download"
            @click.stop="downloadFile(attachment.fileUrl, attachment.name)" />
        </Tooltip>
      </template>
    </PageLink>
  </Tooltip>
  <Tooltip
    v-for="attachment in unsupportedAttachments"
    :key="attachment.duid"
    :text="UNSUPPORTED_FILE_TYPE_TEXT"
    :class="TOOLTIP_CLASS">
    <PageLink
      :mode="mode"
      :icon="isImageType(attachment.kind) ? ImageIcon : AttachmentFieldIcon"
      :icon-args="{ class: INCOMPATIBLE_ICON_CLASS }"
      :title="attachment.name"
      not-in-text
      shrink
      :class="[CHIP_CLASS, INCOMPATIBLE_CHIP_CLASS]"
      @remove="() => removeUnsupportedAttachment(attachment.duid)" />
  </Tooltip>
</template>
